/**
 * Date arithmetic for the Time Management pages. Pure functions over
 * 'YYYY-MM-DD' strings — no data, no holidays.
 *
 * It is its own file so `calendarDays.ts` (which owns the holiday list) and
 * `timeData.ts` (which owns everything else) can both use it without importing
 * each other.
 */

import { businessTodayIso } from '../../lib/businessDate'
import { addDays } from '../../lib/roster/dates'

export { addDays, isWeekend } from '../../lib/roster/dates'

/** 'YYYY-MM-DD' for a local date, which is what every list keys on. */
export function iso(date: Date): string {
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${m}-${d}`
}

/** Parse 'YYYY-MM-DD' as a *local* midnight. `new Date(s)` would read it as
 *  UTC and shift the day backwards for anyone east of Greenwich — which is
 *  everyone here. */
export function parseIso(value: string): Date {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export function addMonths(value: string, months: number): string {
  const date = parseIso(value)
  date.setMonth(date.getMonth() + months, 1)
  return iso(date)
}

/** Today in Ulaanbaatar, not the browser's day. */
export const today = (): string => businessTodayIso()

/** Monday of the week containing `value`. */
export function weekStart(value: string): string {
  const date = parseIso(value)
  // getDay() is 0 for Sunday; shift so Monday is 0.
  const offset = (date.getDay() + 6) % 7
  return addDays(value, -offset)
}

/** First day of the month containing `value`. */
export function monthStart(value: string): string {
  return `${value.slice(0, 7)}-01`
}

/** Every date of the month containing `value`, in order. */
export function monthDates(value: string): string[] {
  const first = monthStart(value)
  const month = first.slice(0, 7)
  const out: string[] = []
  for (let d = first; d.slice(0, 7) === month; d = addDays(d, 1)) out.push(d)
  return out
}

/** Every date of the month containing `value`, padded to whole Mon–Sun weeks
 *  so a calendar grid is always a clean 6x7 or 5x7. */
export function monthGrid(value: string): string[] {
  const first = parseIso(value)
  first.setDate(1)
  const start = weekStart(iso(first))
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0)
  const end = addDays(weekStart(iso(last)), 6)
  const out: string[] = []
  for (let d = start; d <= end; d = addDays(d, 1)) out.push(d)
  return out
}

/** First and last cell of `monthGrid(value)`: the range a calendar that shows
 *  the grid must LOAD, or its neighbour-month cells render blank and clickable. */
export function monthGridRange(value: string): { from: string; to: string } {
  const grid = monthGrid(value)
  return { from: grid[0]!, to: grid[grid.length - 1]! }
}

/** The month to show after the user selects `date`: that day's own month, so a
 *  neighbour-month cell opens with its month summary visible. The
 *  same month string is returned unchanged when nothing moves. */
export function monthAfterSelect(month: string, date: string): string {
  return date.slice(0, 7) === month.slice(0, 7) ? month : date
}

/** 'M/D', the short label the period navigators use. */
export function shortDay(value: string): string {
  const date = parseIso(value)
  return `${date.getMonth() + 1}/${date.getDate()}`
}
